import { useMemo } from 'react';
import { Flex, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { MerkatusLogo } from 'src/components/atoms/MerkatusLogo';
import { useAppContext } from 'src/contexts/AppContext';
import { useModelsListPolling, useModelStatusQuery } from 'src/api/models';
import { ModelProgressBar } from './ModelProgressBar';

export const InitialModelLoadingScreen: React.FC = () => {
  const screenBg = useColorModeValue(
    'rgba(238, 242, 248, 1)',
    'background.dark.lowest',
  );
  const logoTheme = useColorModeValue('dark' as const, 'white' as const);
  const { currentCustomer } = useAppContext();
  const customerName = currentCustomer?.customerName ?? '';

  const { data: models } = useModelsListPolling(customerName);

  const runningModel = useMemo(() => {
    if (!models?.length) return null;
    return models.find(
      (m) =>
        m.quickRun && (m.status === 'running' || m.status === 'not_started'),
    );
  }, [models]);

  const { data: modelStatus } = useModelStatusQuery(
    customerName,
    runningModel?.id ?? null,
  );

  const isRunning = modelStatus?.status === 'running';
  const hasFailed = modelStatus?.status === 'fail';

  const statusMessage = useMemo(() => {
    if (hasFailed) {
      return 'Modeling encountered an error. Please check the details below.';
    }
    if (isRunning) {
      return "Modeling in progress. We're building your dashboard...";
    }
    return 'Preparing your models...';
  }, [isRunning, hasFailed]);

  return (
    <Flex
      flex="1"
      w="100%"
      minH="100vh"
      align="center"
      justify="center"
      bg={screenBg}
      px={{ base: 6, md: 12 }}
      py={{ base: 16, md: 24 }}>
      <VStack
        spacing={{ base: 6, md: 8 }}
        align="center"
        textAlign="center"
        w="100%"
        maxW="500px">
        <MerkatusLogo theme={logoTheme} clickable={false} />

        <VStack spacing={6} w="100%" px={4}>
          <ModelProgressBar
            label={runningModel?.modelName ?? 'Model'}
            status={modelStatus ?? null}
          />
        </VStack>

        <Text fontSize="16px" fontWeight="500" color="text.title" maxW="400px">
          {statusMessage}
        </Text>

        {!hasFailed && (
          <Text fontSize="13px" color="text.muted" maxW="360px">
            You can close this page and come back later. We&apos;ll keep
            processing your models.
          </Text>
        )}
      </VStack>
    </Flex>
  );
};
